import React, { ComponentProps, useCallback, useMemo } from 'react';
import { DatePicker } from '../../components';
import {
  DATE_FORMAT,
  getDayjs,
  ISO_DATE_FORMAT,
  ISO_DATE_TIME_FORMAT,
  ISODate,
} from '../../utils';
import { Dayjs, OpUnitType, QUnitType } from 'dayjs';
import {
  Controller,
  FieldPath,
  FieldValues,
  useFormContext,
} from 'react-hook-form';
import { useAdditionalFormContext } from '../AdditionalFormContext';
import { useGetIdFromName } from '../utils';
import { FORM_DATE_PICKER_POPUP_CLASSNAME } from './constants';

export type FormDateRangePickerProps<
  FormData extends FieldValues = FieldValues,
  Name extends FieldPath<FormData> = FieldPath<FormData>,
> = {
  name: Name;
  valueMode?: 'date' | 'dateTime';
  endOfPeriod?: boolean;
} & Omit<ComponentProps<typeof DatePicker.RangePicker>, 'format'>;

export const FormDateRangePicker = <
  FormData extends FieldValues = FieldValues,
  Name extends FieldPath<FormData> = FieldPath<FormData>,
>(
  props: FormDateRangePickerProps<FormData, Name>,
) => {
  const getIdFromName = useGetIdFromName();
  const {
    name,
    id = getIdFromName(name),
    endOfPeriod = false,
    valueMode = 'date',
    picker,
    ...rest
  } = props;
  const { control } = useFormContext<FormData>();
  const valueFormat =
    valueMode === 'date' ? ISO_DATE_FORMAT : ISO_DATE_TIME_FORMAT;
  const { onChange: formOnChange } = useAdditionalFormContext();

  const parseValue = useCallback(
    (val: string | null) => {
      if (!val) return null;
      return getDayjs(val, valueFormat);
    },
    [valueFormat],
  );

  // show correct placeholder while selecting ranges with endOfPeriod enabled
  const format = useMemo(() => {
    if (!endOfPeriod) return DATE_FORMAT;

    const periodTimeUnit = getTimeUnit(picker);
    return (value: Dayjs) => value.endOf(periodTimeUnit).format(DATE_FORMAT);
  }, [endOfPeriod, picker]);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { value, onChange, disabled } }) => (
        <DatePicker.RangePicker
          disabled={disabled}
          {...rest}
          picker={picker}
          id={{ start: `${id}-0`, end: `${id}-1` }}
          format={format}
          value={[parseValue(value?.[0]), parseValue(value?.[1])]}
          onChange={(val) => {
            const [from, to] = val || [];
            const periodTimeUnit = getTimeUnit(picker);
            const adjustedValue = [
              from?.[endOfPeriod ? 'endOf' : 'startOf'](periodTimeUnit),
              to?.[endOfPeriod ? 'endOf' : 'startOf'](periodTimeUnit),
            ].map((date) => (date ? date.format(valueFormat) : null)) as [
              ISODate | null,
              ISODate | null,
            ];

            onChange({ target: { value: adjustedValue } });
            formOnChange?.();
          }}
          classNames={{
            popup: { root: FORM_DATE_PICKER_POPUP_CLASSNAME },
          }}
        />
      )}
    />
  );
};

function getTimeUnit(picker?: string): OpUnitType | QUnitType {
  let timeUnit: OpUnitType | QUnitType;
  switch (picker) {
    case 'year':
      timeUnit = 'year';
      break;
    case 'quarter':
      timeUnit = 'quarter';
      break;
    case 'month':
      timeUnit = 'month';
      break;
    case 'week':
      timeUnit = 'week';
      break;
    default:
      timeUnit = 'date';
  }

  return timeUnit;
}
